import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {jsonInit} from './client';
import {useAuth} from '../hooks/useAuth';
import {MemoriesPage, PlantDetail, SpeciesDetail} from '../types';

// Plant pages and "this time last year". Query keys (the list itself is usePlants in posts.ts):
//   ['plants', id]               one plant with its seasons
//   ['posts', 'memories', days]  tells from this week in earlier years

export function usePlant(id: string | undefined) {
    const {call} = useAuth();
    return useQuery({queryKey: ['plants', id], queryFn: () => call<PlantDetail>(`/api/plants/${id}`), enabled: !!id, staleTime: 30 * 1000});
}

/** Every variety of one plant name ("garlic"). */
export function useSpecies(key: string | undefined) {
    const {call} = useAuth();
    return useQuery({queryKey: ['plants', 'species', key], queryFn: () => call<SpeciesDetail>(`/api/plants/species/${encodeURIComponent(key ?? '')}`), enabled: !!key, staleTime: 30 * 1000});
}

export function usePlantMutations() {
    const {call} = useAuth();
    const queryClient = useQueryClient();

    const setPermanent = useMutation({
        mutationFn: ({id, isPermanent}: {id: string; isPermanent: boolean}) => call<void>(`/api/plants/${id}`, jsonInit('PATCH', {isPermanent})),
        onMutate: ({id, isPermanent}) => {
            queryClient.setQueryData<PlantDetail>(['plants', id], data =>
                data ? {...data, plant: {...data.plant, isPermanent, group: isPermanent ? 'permanent' : data.plant.group === 'permanent' ? 'past' : data.plant.group}} : data
            );
        },
        onSettled: () => queryClient.invalidateQueries({queryKey: ['plants']})
    });

    // A rename onto another plant's name answers 409 unless merge is set; the caller confirms first.
    const rename = useMutation({
        mutationFn: ({id, label, merge = false}: {id: string; label: string; merge?: boolean}) => call<{id: string; merged: boolean}>(`/api/plants/${id}`, jsonInit('PATCH', {label, merge})),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['plants']});
            queryClient.invalidateQueries({queryKey: ['posts']});
        },
        meta: {silent: true}
    });

    return {setPermanent, rename};
}

export function useMemories(days = 7) {
    const {call} = useAuth();
    return useQuery({
        queryKey: ['posts', 'memories', days],
        queryFn: () => call<MemoriesPage>(`/api/posts/memories?days=${days}`),
        staleTime: 60 * 60 * 1000,
        refetchOnWindowFocus: false
    });
}
